import React, { useState, useContext } from 'react';
import { AppContext } from './App.js'; // Adjust path if needed
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { loadStripe } from '@stripe/stripe-js';
import { Film } from 'lucide-react';

const stripePromise = loadStripe("pk_test_51S35vBKHy89O5Qxfm2d1dZiuwEt8n56dDeKCK1CuhBToTpEkZzqC8abjElD7wOifFDzbFikNidfsBxorTAW9ROaq00POXlqxle"); // Replace with your Publishable Key

const OnboardingPage = () => {
    const { appInstance, purchaseIntent, showMessageBox, setCurrentPage } = useContext(AppContext);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    const planDetails = {
        'indie': { name: 'Indie Tier', price: '$199' },
        'pro': { name: 'Pro Tier', price: '$399' },
        'demo': { name: 'Free Demo', price: '$0' }
    };
    const currentPlan = planDetails[purchaseIntent] || { name: 'Project', price: '' };

    const handleSignUp = async (e) => {
        e.preventDefault();
        setIsProcessing(true);
        try {
            const auth = getAuth();
            await createUserWithEmailAndPassword(auth, email, password);

            // The onAuthStateChanged listener will NOT interfere now.
            // We have full control to proceed to the next step.

            if (purchaseIntent === 'demo') {
                showMessageBox("Success!", "Account created. Setting up your demo project...");
                const functions = getFunctions(appInstance);
                const provisionDemo = httpsCallable(functions, 'provisionDemoProject');
                await provisionDemo();
                // After the demo is created, manually navigate to the projects page.
                setCurrentPage('projects');
            } else {
                // This is a paid user, send them to Stripe.
                const functions = getFunctions(appInstance);
                const createStripeCheckoutSession = httpsCallable(functions, 'createStripeCheckoutSession');
                const result = await createStripeCheckoutSession({ plan: purchaseIntent });
                const { sessionId } = result.data;
                const stripe = await stripePromise;
                await stripe.redirectToCheckout({ sessionId });
            }
        } catch (error) {
            showMessageBox("Onboarding Failed", error.message);
            setIsProcessing(false);
        }
    };

    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-black text-gray-100 p-4 font-poppins">
             <style>{`.neon-purple { text-shadow: 0 0 5px rgba(192, 132, 252, 0.7), 0 0 10px rgba(192, 132, 252, 0.7), 0 0 20px rgba(192, 132, 252, 0.5), 0 0 40px rgba(192, 132, 252, 0.5);}`}</style>
            
            <div className="absolute top-8 text-center">
                <h1 className="text-6xl font-thin text-purple-300 neon-purple">BLACKBOX<span className="text-yellow-400">cr</span></h1>
                <p className="text-sm text-zinc-500 mt-2 tracking-widest">
                    <span className="font-bold text-zinc-200 tracking-wider">closing credits</span>
                </p>
            </div>

            <div className="w-full max-w-sm">
                <div className="text-center mb-6">
                    <p className="text-sm text-purple-400">Step 1 of 2</p>
                    <h2 className="text-2xl font-semibold text-white mt-1">Create Your Account</h2>
                    <p className="text-zinc-400 mt-2">You're purchasing the <span className="font-bold text-white">{currentPlan.name}</span>.</p>
                </div>
                <form onSubmit={handleSignUp}>
                    <div className="space-y-4 mb-6">
                        <input
                            type="email"
                            placeholder="Email Address"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full p-4 rounded-lg border border-zinc-700 bg-zinc-800 text-gray-200 focus:ring-2 focus:ring-purple-500"
                            disabled={isProcessing}
                        />
                        <input
                            type="password"
                            placeholder="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full p-4 rounded-lg border border-zinc-700 bg-zinc-800 text-gray-200 focus:ring-2 focus:ring-purple-500"
                            disabled={isProcessing}
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={isProcessing}
                        className="w-full flex items-center justify-center p-4 rounded-lg font-bold text-lg bg-green-500 text-black hover:bg-green-400 disabled:bg-zinc-700 disabled:cursor-not-allowed"
                    >
                        {isProcessing ? <Film size={24} className="animate-spin" /> : 'Continue'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default OnboardingPage;